import { useEffect, useRef, useState } from "react";
import { ICostInfoDTO } from "../../../../../libs/coreapi-dto/@types/common";
import { IPricingRevaluationItemResultDTO } from "../../../../../libs/coreapi-dto/dirs/pricingInvoice";
import { PricingRevaluationDataProvider } from "../../../../../Services/DataProviders/PricingRevaluationDataProvider";
import { useAppContext } from "../../../../../system/providers/appContextProvider";
import { eventPricingInvoiceTypes } from "../../../Invoice/CollectPricingInvoiceDTO";
import { IPricingModelDTO } from "../../../../../libs/coreapi-dto/dirs/pricingModel";
import { ILotDTO } from "../../../../../libs/coreapi-dto/accounting/lot";
import renderGlobalAlert from "../../../../../system/hooks/useGlobalAlert";
import { IPricingRevaluationItem, IPricingRevaluationItemDTO, IPricingRevaluationSingleItem } from "../../../../../libs/coreapi-dto/dirs/pricingRevaluation";

interface IActRevaluationItemCostInfo {
    quantity: number;
    productMargin: number;
    retailCostInfo: ICostInfoDTO;
    supplierCostInfo: ICostInfoDTO;
}
type calculationActionType = "QuantityChanged" | "AdPriceChanged" | "PriceIncVatChanged" | "RetailTaxTypeChange";
interface ICalculatorAction {
    type: calculationActionType;
    payload: number;
}

interface IRevaluationItemCalculatorArgs extends IActRevaluationItemCostInfo {
    idPricingModel: string;
    pricingModel?: IPricingModelDTO
    isGnvls?: boolean
    lot: ILotDTO;
    renderAlerts: boolean;
    onStartRequest: () => void;
    onEndRequest: () => void;
    onDataReceived: (state: IActRevaluationItemCostInfo, action: calculationActionType) => void;
}

const useRevaluationItemCalculator = (props: IRevaluationItemCalculatorArgs) => {
    const appCtx = useAppContext();
    const pricingRevaluationDP = new PricingRevaluationDataProvider(appCtx.coreApiService);

    const [state, setState] = useState<IActRevaluationItemCostInfo>(props);
    const [action, setAction] = useState<ICalculatorAction>();

    function calculate (action: ICalculatorAction) {
        if (!action) return;

        props.onStartRequest();

        pricingRevaluationDP.calculateItem(
            props.idPricingModel,
            createBody(action),
            (data: IPricingRevaluationItemResultDTO) => {
                if (data.retailPriceChangeReason && data.retailPriceChangeReason.length > 0 && props.renderAlerts) {
                    renderGlobalAlert({
                        variant: data.retailPriceChangeReason.includes("[Info]") ? "info" : "warning",
                        title: data.retailPriceChangeReason.replace("[Info]", ""),
                        statusCode: 0,
                    });
                }

                const newState = {
                    quantity: data.quantity,
                    retailCostInfo: {
                        vat: data.percentageOfVatRateRetail,
                        price: data.retailPrice,
                        vatPrice: data.retailVatPerUnit,
                        priceIncVat: data.retailPriceWithVat,
                        sum: data.retailAmount,
                        vatSum: data.vatAmountRetail,
                        sumIncVat: data.retailAmountWithVat,
                        adprice: data.percentageOfMarkupRetail,
                    },
                    supplierCostInfo: {
                        vat: data.supplierVatRatePercentage,
                        price: data.supplierPriceExcludingVat,
                        vatPrice: data.supplierVatPerUnit,
                        priceIncVat: data.supplierPriceWithVat,
                        sum: data.supplierAmountExcludingVat,
                        vatSum: data.supplierVatAmount,
                        sumIncVat: data.supplierAmountWithVat,
                        adprice: data.supplierMarkupPercentage,
                    },
                    productMargin: data.retailUnitMargin,
                };

                setState(newState);
                props.onDataReceived(newState, action.type);
                props.onEndRequest();
            },
            null,
            (err: string) => {
                props.onEndRequest();
                renderGlobalAlert({
                    variant: "warning",
                    title: err,
                    statusCode: 0,
                });
            }
        );
    };

    useEffect(() => {
        if (!action) return;
        
        calculate(action);

        return () => {
            
        };
    }, [action]);

    const setRetailVat = (vatRate: number) => {
        setState(
            {
                ...state,
                retailCostInfo: {
                    ...state.retailCostInfo,
                    vat: vatRate
                }
            }
        )
    }

    const createBody = (action: ICalculatorAction): IPricingRevaluationItemDTO => {
        const body = createDefaultBody(action);
        switch (action.type) {
            case "QuantityChanged":
                return {
                    ...body,
                    hasAdprice: true,
                    hasRetailPrice: true,
                    revaluationItem: {
                        ...body.revaluationItem,
                        item: {
                            ...body.revaluationItem.item,
                            quantity: action.payload
                        }
                    }
                };
            case "RetailTaxTypeChange":
            case "PriceIncVatChanged":
                return {
                    ...body,
                    hasAdprice: false,
                    hasRetailPrice: true,
                    revaluationItem: {
                        ...body.revaluationItem,
                        item: {
                            ...body.revaluationItem.item,
                            retailCostInfo: {
                                ...body.revaluationItem.item.retailCostInfo,
                                priceIncVat: action.payload
                            }
                        }
                    }
                };
            case "AdPriceChanged":
                return {
                    ...body,
                    hasAdprice: true,
                    hasRetailPrice: false,
                    revaluationItem: {
                        ...body.revaluationItem,
                        item: {
                            ...body.revaluationItem.item,
                            adPrice: action.payload,
                            retailCostInfo: {
                                ...body.revaluationItem.item.retailCostInfo,
                                adprice: action.payload
                            }
                        }
                    }
                };
            default:
                throw new Error("Необработанный тип события");
        }
    };

    const convertToEventType = (actionType: calculationActionType): eventPricingInvoiceTypes => {
        switch (actionType) {
            case "QuantityChanged":
                return "QuantityСhanged";
            case "AdPriceChanged":
                return "PercentageOfMarkupRetailChanged";
            case "PriceIncVatChanged":
            case "RetailTaxTypeChange":
                return "RetailPriceWithVatChanged";
        }
    };

    const createDefaultBody = (action: ICalculatorAction): IPricingRevaluationItemDTO => {

        return {
            eventType: convertToEventType(action.type),
            hasAdprice: true,
            hasRetailPrice: true,
            revaluationItem: {
                idStoreGlobal: props.lot.store?.idGlobal,
                idSupplierGlobal: props.lot.idSupplierGlobal,
                item: {
                    quantity: state.quantity,
                    adPrice: props.lot.retailCostInfo?.adprice,
                    idGoodsGlobal: props.lot.goods.idGlobal,
                    idScalingRatioGlobal: props.lot.scalingRatio.idGlobal,
                    producerPrice: props.lot.producerPrice,
                    registerPrice: props.lot.registerPrice,
                    retailCostInfo: state.retailCostInfo,
                    supplierCostInfo: state.supplierCostInfo,
                    numerator: props.lot.numerator ?? 1,
                    denominator: props.lot.denominator ?? 1,
                } as IPricingRevaluationItem
            } as IPricingRevaluationSingleItem
        } as IPricingRevaluationItemDTO
    };

    return {
        recalculateByAdprice: (newValue: number = state.retailCostInfo.adprice) => {
            setAction({ type: "AdPriceChanged", payload: newValue });
        },
        quantityChange: (newValue: number) => {
            if (state.quantity !== newValue) {
                setAction({ type: "QuantityChanged", payload: newValue });
            }
        },
        adpriceChange: (newValue: number) => {
            if (state.retailCostInfo.adprice !== newValue) {
                setAction({ type: "AdPriceChanged", payload: newValue });
            }
        },
        priceIncVatChange: (newValue: number) => {
            if (state.retailCostInfo.priceIncVat !== newValue) {
                setAction({ type: "PriceIncVatChanged", payload: newValue });
            }
        },
        retailTaxTypeChange: (newValue: number) => {
            if (state.retailCostInfo.vat !== newValue) {
                setAction({ type: "RetailTaxTypeChange", payload: newValue });
            }
        },
        setRetailVat: (vatRate: number) => setRetailVat(vatRate)
    };
};

export default useRevaluationItemCalculator;
